import { useEffect, useRef, useState } from "react";
import { ActionIcon, Popover, TextInput, TextInputProps } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Поле даты, заточенное под основной use-case заказчика:
 * данные ВСЕГДА приходят текстом и копипастятся из других систем.
 * Mantine DateInput ловит только маска-ввод "ДД.ММ.ГГГГ" и роняет paste
 * "06.05.2010", "2010-05-06", "6/5/2010", "06 мая 2010" и т.п. —
 * пользователь в итоге не может ввести дату вообще.
 *
 * Эта обёртка — обычный TextInput с маленьким парсером:
 *  - пустая строка → null;
 *  - перебираем известные форматы через dayjs (strict);
 *  - на blur нормализуем visible value к "ДД.ММ.ГГГГ", если распарсилось.
 *
 * Подходит для любых дат — никаких неотлавливаемых ввод-кейсов.
 */
const ACCEPTED_FORMATS = [
  "DD.MM.YYYY",
  "D.M.YYYY",
  "DD.MM.YY",
  "D.M.YY",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD/MM/YY",
  "D/M/YY",
  "DD-MM-YYYY",
  "YYYY-MM-DD",
  "YYYY.MM.DD",
  "YYYY/MM/DD",
];

const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

function parseAny(input: string): Date | null {
  const s = input.trim();
  if (!s) return null;

  // strict-парсинг по нашему списку, чтобы "abc" не превращалось в Invalid Date.
  for (const fmt of ACCEPTED_FORMATS) {
    const d = dayjs(s, fmt, true);
    if (d.isValid() && d.year() >= MIN_YEAR && d.year() <= MAX_YEAR) {
      return d.toDate();
    }
  }
  return null;
}

function formatDate(d: Date | null | undefined): string {
  if (!d) return "";
  const x = dayjs(d);
  return x.isValid() ? x.format("DD.MM.YYYY") : "";
}

/**
 * Live-маска при наборе. Превращает «12122025» в «12.12.2025» по мере набора,
 * а так же мирно ест «12.12.2025», «12/12/2025», «2025-12-12» (paste).
 *
 * Алгоритм:
 *  1) Считаем цифры в строке. Применяем dayjs strict-парсинг ТОЛЬКО если их
 *     ровно 8 — иначе при стирании с «12.12.2024» до «12.12.20» (6 цифр)
 *     dayjs сматчит формат «DD.MM.YY» и вернёт обратно «12.12.2020», т.е.
 *     год сам «дорастёт» обратно до 4 цифр. Это и есть баг заказчика.
 *  2) Если 8 цифр — пытаемся dayjs (поддерживаем paste «2025-12-12»,
 *     «12/12/2025» и т.п.). Если матчит — возвращаем «DD.MM.YYYY».
 *  3) Если dayjs не матчит ИЛИ цифр меньше/больше 8 — просто live-mask:
 *     режем по 8 цифр, ставим точки после 2-й и 4-й.
 */
function applyMask(input: string): string {
  const s = input.trim();
  if (!s) return "";

  const allDigits = s.replace(/\D+/g, "");

  // Strict-парсинг только когда дата выглядит «полной».
  // 8 цифр это и DD.MM.YYYY, и YYYY-MM-DD, и 12122025 без разделителей.
  if (allDigits.length === 8) {
    for (const fmt of ACCEPTED_FORMATS) {
      const d = dayjs(s, fmt, true);
      if (d.isValid() && d.year() >= MIN_YEAR && d.year() <= MAX_YEAR) {
        return d.format("DD.MM.YYYY");
      }
    }
  }

  // Live-mask: оставляем только цифры, режем по 8, расставляем точки.
  const digits = allDigits.slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return digits.slice(0, 2) + "." + digits.slice(2);
  return digits.slice(0, 2) + "." + digits.slice(2, 4) + "." + digits.slice(4);
}

export interface SafeDateInputProps
  extends Omit<TextInputProps, "value" | "onChange" | "type"> {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  /** Если форма Mantine отдаёт error через getInputProps — пробрасываем. */
  error?: React.ReactNode;
  /** Принимаем legacy-пропсы от Mantine DateInput (игнорируются). */
  valueFormat?: string;
  clearable?: boolean;
  minDate?: Date;
  maxDate?: Date;
  defaultLevel?: string;
  defaultDate?: Date;
  popoverProps?: Record<string, unknown>;
}

export function SafeDateInput({
  value,
  onChange,
  error,
  placeholder = "дд.мм.гггг",
  rightSection,
  valueFormat: _vf,
  clearable: _cl,
  minDate: _min,
  maxDate: _max,
  defaultLevel: _dl,
  defaultDate: _dd,
  popoverProps: _pp,
  ...rest
}: SafeDateInputProps) {
  // Внутренний текст. Отдельно от parsed-Date, чтобы пользователь мог печатать
  // частичную «06.05.» и не терять её при ререндере родителя.
  const [text, setText] = useState<string>(() => formatDate(value));
  const [localError, setLocalError] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const lastNotified = useRef<number | null>(null);

  // Когда родитель сменил value (например, после fetch'а данных) — синхронизируем.
  useEffect(() => {
    const next = formatDate(value);
    // Чтобы не мешать набору, обновляем только если значение реально другое
    // (с округлением до дня).
    const cur = parseAny(text);
    const curTs = cur ? dayjs(cur).startOf("day").valueOf() : null;
    const newTs = value ? dayjs(value).startOf("day").valueOf() : null;
    if (curTs !== newTs) {
      setText(next);
      lastNotified.current = newTs;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.toString()]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Live-маска: пользователь печатает «12122025», в поле появляется
    // «12.12.2025». Если он стирает символ — мы тоже корректно ужимаемся
    // (потому что applyMask работает по цифрам, а не по позиции каретки).
    const masked = applyMask(e.currentTarget.value);
    setText(masked);
    setLocalError(null);

    // Пытаемся распарсить — если дата полная, сразу сообщаем родителю.
    // Если нет (например, ввели только «12.12.»), родителю даём null,
    // но БЕЗ ошибки в UI — пользователь ещё печатает.
    const parsed = parseAny(masked);
    const parsedTs = parsed ? dayjs(parsed).startOf("day").valueOf() : null;
    if (parsedTs !== lastNotified.current) {
      lastNotified.current = parsedTs;
      onChange?.(parsed);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const s = text.trim();
    if (!s) {
      setLocalError(null);
      onChange?.(null);
      rest.onBlur?.(e);
      return;
    }
    const parsed = parseAny(s);
    if (!parsed) {
      // Подскажем, что мы ждали — не оставляем тихое «ничего не сохранилось».
      setLocalError("Дата непонятна. Пример: 06.05.2010 или 2010-05-06");
      rest.onBlur?.(e);
      return;
    }
    setLocalError(null);
    // Нормализуем visible value к каноническому формату.
    setText(formatDate(parsed));
    lastNotified.current = dayjs(parsed).startOf("day").valueOf();
    onChange?.(parsed);
    rest.onBlur?.(e);
  };

  // Иконка-кнопка справа: открывает popover с календарём. Дефолтная дата
  // календаря — текущая введённая (если parsed) или сегодня. Это запрос
  // заказчика — даёт быструю мышиную альтернативу ручному вводу.
  const currentDate = parseAny(text) ?? value ?? new Date();
  const calendarButton = (
    <Popover
      opened={popoverOpen}
      onChange={setPopoverOpen}
      position="bottom-end"
      withinPortal
      shadow="md"
    >
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => setPopoverOpen((o) => !o)}
          aria-label="Открыть календарь"
        >
          <IconCalendar size={16} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <DatePicker
          value={parseAny(text)}
          defaultDate={currentDate}
          onChange={(d: Date | string | null) => {
            // Mantine v7 даёт Date | null или string в зависимости от версии.
            const next = d instanceof Date ? d : d ? new Date(d) : null;
            if (next) {
              setText(formatDate(next));
              lastNotified.current = dayjs(next).startOf("day").valueOf();
              onChange?.(next);
            } else {
              setText("");
              lastNotified.current = null;
              onChange?.(null);
            }
            setLocalError(null);
            setPopoverOpen(false);
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );

  return (
    <TextInput
      {...rest}
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      error={error ?? localError ?? undefined}
      rightSection={rightSection ?? calendarButton}
      autoComplete="off"
      inputMode="numeric"
    />
  );
}
